import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Calendar, User, Loader2, FileText, Flame, ArrowRight } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { TeamSnapshot } from '../components/custom/TeamSnapshot';
import { FanPoll, Storylines } from '../components/custom/TeamHubSections';
import { useSeo } from '../components/custom/Seo';
import axios from 'axios';
import { logError } from '../lib/logger';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const teamData = {
  commanders: {
    name: 'Commanders',
    fullName: 'Washington Commanders',
    sport: 'NFL Football',
    primaryColor: '#5A1414',
    secondaryColor: '#FFB612',
    textColor: '#FFFFFF',
  },
  capitals: {
    name: 'Capitals',
    fullName: 'Washington Capitals',
    sport: 'NHL Hockey',
    primaryColor: '#C8102E',
    secondaryColor: '#041E42',
    textColor: '#FFFFFF',
  },
  nationals: {
    name: 'Nationals',
    fullName: 'Washington Nationals',
    sport: 'MLB Baseball',
    primaryColor: '#AB0003',
    secondaryColor: '#14225A',
    textColor: '#FFFFFF',
  },
  wizards: {
    name: 'Wizards',
    fullName: 'Washington Wizards',
    sport: 'NBA Basketball',
    primaryColor: '#002B5C',
    secondaryColor: '#E31837',
    textColor: '#FFFFFF',
  },
  mystics: {
    name: 'Mystics',
    fullName: 'Washington Mystics',
    sport: 'WNBA Basketball',
    primaryColor: '#002B5C',
    secondaryColor: '#C8102E',
    textColor: '#FFFFFF',
  },
  dcunited: {
    name: 'DC United',
    fullName: 'DC United',
    sport: 'MLS Soccer',
    primaryColor: '#000000',
    secondaryColor: '#EF3E42',
    textColor: '#FFFFFF',
  },
  spirit: {
    name: 'Spirit',
    fullName: 'Washington Spirit',
    sport: 'NWSL Soccer',
    primaryColor: '#C4122E',
    secondaryColor: '#1A1A1A',
    textColor: '#FFFFFF',
  },
  defenders: {
    name: 'Defenders',
    fullName: 'DC Defenders',
    sport: 'UFL Football',
    primaryColor: '#C8102E',
    secondaryColor: '#000000',
    textColor: '#FFFFFF',
  },
};

const TeamPage = ({ teamKey }) => {
  const [articles, setArticles] = useState([]);
  const [loading, setLoading] = useState(true);
  const team = teamData[teamKey];

  useSeo({
    title: team ? `${team.fullName} News | DC Sports Report` : undefined,
    description: team
      ? `The latest ${team.fullName} news, analysis and coverage from DC Sports Report, an independent Washington, D.C. sports publication.`
      : undefined,
    path: `/${teamKey}`,
  });

  useEffect(() => {
    const fetchArticles = async () => {
      try {
        const response = await axios.get(`${API}/articles/team/${teamKey}`);
        setArticles(response.data.articles || []);
      } catch (error) {
        logError('Error fetching articles:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchArticles();
  }, [teamKey]);

  const formatDate = (dateString) => {
    if (!dateString) return '';
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('en-US', { 
        year: 'numeric', 
        month: 'short', 
        day: 'numeric'
      });
    } catch {
      return dateString;
    }
  };

  const isNew = (dateString) => {
    const articleDate = new Date(dateString);
    const weekAgo = new Date();
    weekAgo.setDate(weekAgo.getDate() - 7);
    return articleDate >= weekAgo;
  };

  if (!team) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <p>Team not found</p>
      </div>
    );
  }

  // If no articles, show coming soon page
  if (!loading && articles.length === 0) {
    return (
      <div 
        data-testid={`team-page-${teamKey}`} 
        className="min-h-screen flex flex-col"
        style={{ backgroundColor: team.primaryColor }}
      >
        <div className="flex-1 flex flex-col items-center justify-center px-4 text-center">
          <div className="absolute top-24 left-4 sm:left-8">
            <Link to="/">
              <Button
                variant="outline"
                data-testid="back-to-home-button"
                className="bg-black/20 hover:bg-black/40 text-white border-white/30 hover:border-white"
              >
                <ArrowLeft className="w-4 h-4 mr-2" />
                Back to Home
              </Button>
            </Link>
          </div>

          <div 
            className="px-6 py-2 text-sm font-bold uppercase tracking-widest mb-6 border-2"
            style={{ 
              backgroundColor: team.secondaryColor,
              color: team.textColor,
              borderColor: team.textColor
            }}
          >
            {team.sport}
          </div>

          <h1 
            className="font-heading text-5xl sm:text-6xl md:text-7xl lg:text-8xl uppercase tracking-tight leading-none mb-8"
            style={{ color: team.textColor }}
          >
            {team.fullName}
          </h1>

          <div 
            className="px-8 py-4 border-4 shadow-brutal-lg animate-flash"
            style={{ 
              backgroundColor: team.secondaryColor,
              borderColor: team.textColor
            }}
          >
            <p 
              className="font-heading text-2xl sm:text-3xl md:text-4xl uppercase tracking-wide"
              style={{ color: team.textColor }}
            >
              Full Coverage Coming Soon
            </p>
          </div>

          <p 
            className="mt-8 text-lg sm:text-xl max-w-md opacity-80"
            style={{ color: team.textColor }}
          >
            We're working on bringing you the best {team.name} coverage. 
            Check back soon for news, analysis, and more!
          </p>

          <Link to="/#newsletter" className="mt-8">
            <Button
              className="h-14 px-8 font-bold uppercase tracking-wide border-2 shadow-brutal hover:shadow-brutal-lg hover:-translate-y-1 transition-all"
              style={{ 
                backgroundColor: team.secondaryColor,
                color: team.textColor,
                borderColor: team.textColor
              }}
            >
              Subscribe for Updates
            </Button>
          </Link>

          <div className="mt-10 w-full max-w-xl text-left space-y-6">
            <TeamSnapshot teamKey={teamKey} />
            <Storylines teamKey={teamKey} />
            <FanPoll teamKey={teamKey} />
          </div>
        </div>

        <div 
          className="h-4"
          style={{ backgroundColor: team.secondaryColor }}
        />
      </div>
    );
  }

  const featuredArticle = articles[0];
  const otherArticles = articles.slice(1);

  // Show articles
  return (
    <div 
      data-testid={`team-page-${teamKey}`} 
      className="min-h-screen bg-slate-100"
    >
      {/* Hero Header */}
      <div 
        className="py-12 sm:py-16"
        style={{ backgroundColor: team.primaryColor }}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Link to="/">
            <Button
              variant="outline"
              data-testid="back-to-home-button"
              className="mb-6 bg-black/20 hover:bg-black/40 text-white border-white/30 hover:border-white"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back to Home
            </Button>
          </Link>
          
          <div 
            className="inline-block px-4 py-1 text-xs font-bold uppercase tracking-widest mb-4 border-2"
            style={{ 
              backgroundColor: team.secondaryColor,
              color: 'white',
              borderColor: 'white'
            }}
          >
            {team.sport}
          </div>
          
          <h1 className="font-heading text-4xl sm:text-5xl md:text-6xl text-white uppercase tracking-tight leading-none">
            {team.fullName}
          </h1>
        </div>
      </div>

      {/* Featured Article */}
      {featuredArticle && (
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 -mt-8">
          <Link to={`/article/${featuredArticle.id}`}>
            <Card 
              className="border-4 border-black shadow-brutal-lg hover:-translate-y-2 transition-all cursor-pointer overflow-hidden"
              style={{ borderTopColor: team.primaryColor, borderTopWidth: '8px' }}
            >
              <CardContent className="p-0">
                <div className="grid md:grid-cols-3">
                  {featuredArticle.has_image ? (
                    <div className="md:col-span-1 bg-slate-200 min-h-[200px]">
                      <img
                        src={`${API}/articles/${featuredArticle.id}/image`}
                        alt={featuredArticle.title}
                        className="w-full h-full object-cover"
                      />
                    </div>
                  ) : (
                    <div 
                      className="p-8 flex flex-col justify-center"
                      style={{ backgroundColor: team.primaryColor }}
                    >
                      <div className="flex items-center gap-2 mb-4">
                        {isNew(featuredArticle.created_at) && (
                          <span className="bg-[#FFB612] text-[#002868] px-3 py-1 text-xs font-bold uppercase tracking-wide animate-pulse flex items-center gap-1">
                            <Flame className="w-3 h-3" />
                            New
                          </span>
                        )}
                        <span className="text-white/70 text-sm font-bold uppercase">Featured</span>
                      </div>
                      <h2 className="font-heading text-2xl sm:text-3xl text-white uppercase tracking-tight leading-tight">
                        {featuredArticle.title}
                      </h2>
                      <div className="mt-4 flex items-center gap-4 text-white/70 text-sm">
                        <span className="flex items-center gap-1">
                          <User className="w-4 h-4" />
                          {featuredArticle.author}
                        </span>
                        <span className="flex items-center gap-1">
                          <Calendar className="w-4 h-4" />
                          {formatDate(featuredArticle.created_at)}
                        </span>
                      </div>
                    </div>
                  )}
                  
                  {/* Right side - content preview */}
                  <div className="md:col-span-2 p-8 bg-white flex flex-col justify-center">
                    {featuredArticle.has_image && (
                      <div className="flex items-center gap-2 mb-3">
                        {isNew(featuredArticle.created_at) && (
                          <span className="bg-[#FFB612] text-[#002868] px-3 py-1 text-xs font-bold uppercase tracking-wide animate-pulse flex items-center gap-1">
                            <Flame className="w-3 h-3" />
                            New
                          </span>
                        )}
                        <span className="text-xs font-bold uppercase tracking-wide" style={{ color: team.primaryColor }}>Featured</span>
                      </div>
                    )}
                    {featuredArticle.has_image && (
                      <h2 className="font-heading text-2xl sm:text-3xl text-slate-900 uppercase tracking-tight leading-tight mb-3">
                        {featuredArticle.title}
                      </h2>
                    )}
                    <p className="text-slate-600 text-lg leading-relaxed line-clamp-3 mb-6">
                      {featuredArticle.summary || featuredArticle.content.substring(0, 250) + '...'}
                    </p>
                    {featuredArticle.has_image && (
                      <div className="mb-4 flex items-center gap-4 text-slate-500 text-sm">
                        <span className="flex items-center gap-1"><User className="w-4 h-4" />{featuredArticle.author}</span>
                        <span className="flex items-center gap-1"><Calendar className="w-4 h-4" />{formatDate(featuredArticle.created_at)}</span>
                      </div>
                    )}
                    <div className="flex items-center gap-2 text-[#002868] font-bold uppercase tracking-wide group">
                      Read Full Article 
                      <ArrowRight className="w-5 h-5 group-hover:translate-x-2 transition-transform" />
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </Link>
        </div>
      )}

      {/* Current Season Snapshot + Fan Engagement */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-10">
        <div className="grid md:grid-cols-2 gap-6 items-start">
          <TeamSnapshot teamKey={teamKey} />
          <div className="space-y-6">
            <Storylines teamKey={teamKey} />
            <FanPoll teamKey={teamKey} />
          </div>
        </div>
      </div>

      {/* Other Articles */}
      {otherArticles.length > 0 && (
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <h2 className="font-heading text-2xl text-slate-900 uppercase tracking-tight mb-8 flex items-center gap-2">
            <FileText className="w-6 h-6" style={{ color: team.primaryColor }} />
            More Articles
          </h2>

          <div className="grid md:grid-cols-2 gap-6">
            {otherArticles.map((article) => (
              <Link key={article.id} to={`/article/${article.id}`}>
                <Card 
                  className="h-full border-2 border-slate-200 hover:border-black transition-all hover:-translate-y-1 shadow-sm hover:shadow-brutal cursor-pointer group overflow-hidden"
                >
                  {article.has_image && (
                    <div className="w-full h-44 overflow-hidden bg-slate-200">
                      <img
                        src={`${API}/articles/${article.id}/image`}
                        alt={article.title}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                      />
                    </div>
                  )}
                  <CardContent className="p-6">
                    <div className="flex items-center gap-2 mb-3">
                      {isNew(article.created_at) && (
                        <span className="bg-[#FFB612] text-[#002868] px-2 py-1 text-xs font-bold uppercase tracking-wide animate-pulse flex items-center gap-1">
                          <Flame className="w-3 h-3" />
                          New
                        </span>
                      )}
                      <span 
                        className="px-3 py-1 text-xs font-bold uppercase tracking-wide"
                        style={{ 
                          backgroundColor: team.primaryColor + '15',
                          color: team.primaryColor 
                        }}
                      >
                        {team.name}
                      </span>
                    </div>
                    
                    <h3 className="font-heading text-xl text-slate-900 uppercase tracking-tight mb-3 group-hover:text-[#002868] transition-colors">
                      {article.title}
                    </h3>
                    
                    {article.summary && (
                      <p className="text-slate-600 mb-4 line-clamp-2">
                        {article.summary}
                      </p>
                    )}
                    
                    <div className="flex flex-wrap items-center gap-4 text-sm text-slate-500">
                      <span className="flex items-center gap-1">
                        <User className="w-4 h-4" />
                        {article.author}
                      </span>
                      <span className="flex items-center gap-1">
                        <Calendar className="w-4 h-4" />
                        {formatDate(article.created_at)}
                      </span>
                    </div>
                  </CardContent>
                </Card>
              </Link>
            ))}
          </div>
        </div>
      )}

      {/* Bottom Accent */}
      <div 
        className="h-4"
        style={{ backgroundColor: team.secondaryColor }}
      />
    </div>
  );
};

export default TeamPage;
